/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {useState, useEffect, useCallback, useRef} from 'react';
import {callOpenRouterApi} from '../../services/openrouterApi';
import {UserProfile, ChatMessage as ChatMessageType} from '../../types';
import {getMCQExamPromptWithCourseData} from '../../utils/prompts';
import {supabase} from '../../services/supabase';
import ChatMessage from '../common/ChatMessage';
import InputField from '../common/InputField';
import MarkdownRenderer from '../common/MarkdownRenderer';
import {useTTS} from '../../contexts/TTSContext';
import MCQFeedbackDashboard from './MCQFeedbackDashboard';
import '../../styles/components/MCQExamSimulator.css';

interface ExamQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

interface ExamState {
  questions: ExamQuestion[];
  currentQuestion: number;
  answers: number[];
  confidenceLevels: ('High' | 'Medium' | 'Low' | null)[];
  isComplete: boolean;
  score: number;
  startTime: Date;
  endTime: Date | null;
  showResults: boolean;
  timeTaken: number;
  readinessScore: number;
  predictedOutcome: 'Fail Risk' | 'Borderline' | 'Likely Pass';
  enhancedFeedback?: {
    performance: {
      score: number;
      percentage: number;
      readinessScore: number;
      predictedOutcome: "Fail Risk" | "Borderline" | "Likely Pass";
    };
    weakTopics: Array<{ topic: string; priority: 'HIGH' | 'MEDIUM'; missedCount: number }>;
    strongTopics: string[];
    actionPlan: Array<{ step: number; topic: string; priority: 'HIGH' | 'MEDIUM'; action: string }>;
    performanceInsights: {
      recallStrength: number;
      speedEfficiency: number;
      confidenceAccuracyGap: number;
      blankRisk: number;
    };
    patternInsight?: string;
  };
}

interface ExamSession {
  id: string;
  title: string;
  timestamp: Date;
  messages: ChatMessageType[];
  examLevel: 'secondary' | 'university';
  config: ExamConfig;
  isTemporary?: boolean;
  exam_completed?: boolean;
  exam_results?: string;
  score_percentage?: number;
  correct_answers?: number;
  total_questions?: number;
  time_taken_minutes?: number;
  integrity_metrics?: {
    copyAttempts: number;
    pasteAttempts: number;
    tabSwitches: number;
  };
  readiness_score?: number;
  confidence_level?: 'Low' | 'Moderate' | 'High';
  enhanced_feedback?: any;
}

interface ExamConfig {
  examLevel: 'secondary' | 'university';
  questionCount: number;
  duration: number;
  showConfidenceCheck: boolean;
  
  // Simplified for registered courses
  fieldOfStudy: string;
  level: string;
  examConfidence: 'Low' | 'Medium' | 'High';
}

interface RegisteredCourse {
  id: string;
  course_name: string;
  course_code: string;
  syllabus: string;
  lecturer_keywords: string[];
}

interface MCQExamSimulatorProps {
  userProfile: UserProfile;
  sessionId?: string;
}

// Utility functions
const parseNumberInput = (value: string, max: number, defaultValue: number): number => {
  if (value === '') return defaultValue;
  const num = parseInt(value);
  if (isNaN(num)) return defaultValue;
  return Math.min(max, num);
};

const calculateSpeedEfficiency = (timeTaken: number, totalTime: number): number => {
  const timeRatio = timeTaken / totalTime;
  if (timeRatio <= 0.5) return 100;
  if (timeRatio <= 0.75) return 75;
  if (timeRatio <= 1) return 50;
  return 25;
};

const calculateRecallStrength = (confidenceLevels: ('High' | 'Medium' | 'Low' | null)[]): number => {
  const validConfidences = confidenceLevels.filter(c => c !== null);
  if (validConfidences.length === 0) return 50;
  
  const scores = validConfidences.map(c => {
    switch(c) {
      case 'High': return 90;
      case 'Medium': return 60;
      case 'Low': return 30;
      default: return 50;
    }
  });
  
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
};

const MCQExamSimulator: React.FC<MCQExamSimulatorProps> = ({userProfile, sessionId}) => {
  // State declarations
  const [sessions, setSessions] = useState<ExamSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [errorDetails, setErrorDetails] = useState<string>('');
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCorrectAnswers, setShowCorrectAnswers] = useState(false);
  
  // Registered courses state
  const [registeredCourses, setRegisteredCourses] = useState<RegisteredCourse[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<RegisteredCourse | null>(null);
  const [loadingCourses, setLoadingCourses] = useState(false);
  
  // Exam configuration states - simplified (removed level selection)
  const [examConfig, setExamConfig] = useState<ExamConfig>({
    examLevel: 'university',
    questionCount: 10,
    duration: 10,
    showConfidenceCheck: true,
    fieldOfStudy: '',
    level: 'Undergraduate',
    examConfidence: 'Medium'
  });

  // Temporary state for number inputs
  const [tempQuestionCount, setTempQuestionCount] = useState<string>('10');
  const [tempDuration, setTempDuration] = useState<string>('10');
  
  // Exam-specific states
  const [examState, setExamState] = useState<ExamState | null>(null);
  const [examStarted, setExamStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [examIntegrity, setExamIntegrity] = useState({
    copyAttempts: 0,
    pasteAttempts: 0,
    tabSwitches: 0,
    lastFocusTime: Date.now(),
    lastTabSwitch: Date.now()
  });
  const [showConfidenceCheck, setShowConfidenceCheck] = useState(true);

  // Chat functionality
  const [chatInput, setChatInput] = useState('');
  const {speak, isMessageSpeaking} = useTTS();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // Load registered courses
  useEffect(() => {
    loadRegisteredCourses();
  }, []);

  const loadRegisteredCourses = async () => {
    setLoadingCourses(true);
    try {
      const {data: {user}, error: authError} = await supabase.auth.getUser();
      if (authError || !user) {
        console.log('User not authenticated');
        return;
      }

      const {data: courses, error: coursesError} = await supabase
        .from('courses')
        .select('id, course_name, course_code, syllabus, lecturer_keywords')
        .eq('user_id', user.id)
        .order('created_at', {ascending: false});

      if (coursesError) {
        console.error('Error loading courses:', coursesError);
        return;
      }

      setRegisteredCourses(courses || []);
    } catch (error) {
      console.error('Error in loadRegisteredCourses:', error);
    } finally {
      setLoadingCourses(false);
    }
  };

  // Sync temporary states with config
  useEffect(() => {
    setTempQuestionCount(examConfig.questionCount.toString());
    setTempDuration(examConfig.duration.toString());
  }, [examConfig]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    isMountedRef.current = true;
    
    const loadData = async () => {
      if (isMountedRef.current) {
        await loadSessions();
      }
    };
    
    loadData();
    
    const handleResize = () => {
      if (isMountedRef.current) {
        setSidebarOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const loadSessions = async () => {
    try {
      const {data: {user}, error: authError} = await supabase.auth.getUser();
      if (authError || !user) {
        console.log('User not authenticated');
        return;
      }

      const {data: sessionData, error: sessionError} = await supabase
        .from('exam_sessions')
        .select('*')
        .eq('user_id', user.id)
        .eq('exam_type', 'mcq-simulator')
        .order('created_at', {ascending: false});

      if (sessionError) {
        console.error('Error loading exam sessions:', sessionError);
        return;
      }

      const loadedSessions: ExamSession[] = [];
      
      for (const session of sessionData) {
        try {
          // IMPORTANT: Use 'session_id' column name for exam_messages
          const {data: msgs, error: msgError} = await supabase
            .from('exam_messages')
            .select('*')
            .eq('session_id', session.id)
            .order('created_at', {ascending: true});

          const sessionObj: ExamSession = {
            id: session.id,
            title: session.title,
            timestamp: new Date(session.created_at),
            messages: msgs?.map(msg => ({
              id: msg.id?.toString() || Date.now().toString(),
              role: msg.role,
              content: msg.content,
              timestamp: new Date(msg.created_at),
              chat_type: 'mcq-exam',
            })) || [],
            examLevel: session.exam_level || 'university',
            config: session.config,
            exam_completed: session.exam_completed,
            exam_results: session.exam_results,
            score_percentage: session.score_percentage,
            correct_answers: session.correct_answers,
            total_questions: session.total_questions,
            time_taken_minutes: session.time_taken_minutes,
            integrity_metrics: session.integrity_metrics,
            readiness_score: session.readiness_score,
            confidence_level: session.confidence_level,
            enhanced_feedback: session.enhanced_feedback,
            isTemporary: false
          };
          
          loadedSessions.push(sessionObj);
        } catch (error) {
          console.error('Error processing session:', error);
        }
      }

      if (isMountedRef.current) {
        setSessions(loadedSessions);

        if (loadedSessions.length > 0) {
          const initialSession = sessionId 
            ? loadedSessions.find(s => s.id === sessionId) || loadedSessions[0]
            : loadedSessions[0];
          setCurrentSessionId(initialSession.id);
          setMessages(initialSession.messages);
          
          if (initialSession.exam_completed) {
            setExamStarted(true);
          }
        } else {
          createNewSession();
        }
      }
    } catch (error) {
      console.error('Error in loadSessions:', error);
    }
  };

  const createNewSession = () => {
    const tempSession: ExamSession = {
      id: `temp-${Date.now()}`,
      title: 'New MCQ Exam',
      timestamp: new Date(),
      messages: [],
      examLevel: 'university',
      config: examConfig,
      isTemporary: true,
      exam_completed: false,
    };
    
    if (isMountedRef.current) {
      setSessions(prev => [tempSession, ...prev]);
      setCurrentSessionId(tempSession.id);
      setMessages([]);
      setExamStarted(false);
      setExamState(null);
      setShowCorrectAnswers(false);
    }
  };

  // IMPORTANT: Fixed column name to 'session_id' (not 'exam_session_id')
  const saveChatMessage = async (sessionId: string, role: 'user' | 'assistant', content: string) => {
    if (!sessionId || sessionId.startsWith('temp-')) return null;

    try {
      const {data, error} = await supabase
        .from('exam_messages')
        .insert({
          session_id: sessionId,  // Correct column name
          role,
          content,
          created_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) {
        console.error('Error saving message:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in saveChatMessage:', error);
      return null;
    }
  };

  const saveExamSession = async (session: ExamSession) => {
    const {data: {user}, error: authError} = await supabase.auth.getUser();
    if (authError || !user) {
      setErrorMessage('Failed to authenticate user. Please log in again.');
      return null;
    }

    try {
      const {data, error} = await supabase
        .from('exam_sessions')
        .insert({
          user_id: user.id,
          title: session.title,
          exam_type: 'mcq-simulator',
          created_at: session.timestamp.toISOString(),
          exam_level: session.examLevel,
          config: session.config,
          question_count: session.config.questionCount,
          duration_minutes: session.config.duration,
          exam_completed: session.exam_completed || false,
          exam_results: session.exam_results,
          score_percentage: session.score_percentage,
          correct_answers: session.correct_answers,
          total_questions: session.total_questions,
          time_taken_minutes: session.time_taken_minutes,
          integrity_metrics: session.integrity_metrics,
          readiness_score: session.readiness_score,
          confidence_level: session.confidence_level,
          enhanced_feedback: session.enhanced_feedback,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating exam session:', error);
        setErrorMessage('Error creating exam session: ' + error.message);
        return null;
      }

      return {...session, id: data.id, isTemporary: false};
    } catch (error) {
      console.error('Error in saveExamSession:', error);
      return null;
    }
  };

  const updateExamSessionResults = async (sessionId: string, results: any) => {
    if (!sessionId || sessionId.startsWith('temp-')) return;

    try {
      const {error} = await supabase
        .from('exam_sessions')
        .update(results)
        .eq('id', sessionId);

      if (error) {
        console.error('Error updating exam results:', error);
      }
    } catch (error) {
      console.error('Error in updateExamSessionResults:', error);
    }
  };

  const calculateScore = useCallback(async (answers: number[], examState: ExamState | null, confidenceLevels: ('High' | 'Medium' | 'Low' | null)[]) => {
    if (!examState) return;

    const correctCount = answers.filter((ans, idx) => 
      ans === examState.questions[idx].correctAnswer
    ).length;

    const score = Math.round((correctCount / examState.questions.length) * 100);
    const timeTaken = Math.round((Date.now() - examState.startTime.getTime()) / 1000 / 60);
    const totalTime = examConfig.duration;
    const speedEfficiency = calculateSpeedEfficiency(timeTaken, totalTime);
    const recallStrength = calculateRecallStrength(confidenceLevels);
    
    const readinessScore = Math.min(100, Math.max(0, Math.round(
      (score * 0.6) + (speedEfficiency * 0.25) + (recallStrength * 0.15)
    )));
    
    let predictedOutcome: 'Likely Pass' | 'Borderline' | 'Fail Risk';
    let overallConfidence: 'High' | 'Moderate' | 'Low';
    
    if (readinessScore >= 80) {
      predictedOutcome = 'Likely Pass';
      overallConfidence = 'High';
    } else if (readinessScore >= 60) {
      predictedOutcome = 'Borderline';
      overallConfidence = 'Moderate';
    } else {
      predictedOutcome = 'Fail Risk';
      overallConfidence = 'Low';
    }
    
    const blankRiskQuestions = answers.filter((ans, idx) => 
      ans === -1 || confidenceLevels[idx] === 'Low'
    ).length;
    const blankRisk = Math.round((blankRiskQuestions / examState.questions.length) * 100);
    
    // Get topics from selected course
    const courseTopics = selectedCourse?.syllabus 
      ? selectedCourse.syllabus.split(',').map(t => t.trim()).filter(t => t.length > 0)
      : ['General Concepts', 'Key Principles', 'Applications', 'Problem Solving'];
    
    const weakTopics: { topic: string; priority: 'HIGH' | 'MEDIUM'; missedCount: number }[] = [];
    const strongTopics: string[] = [];
    
    // Analyze topics based on answers
    courseTopics.forEach((topic, idx) => {
      const topicQuestionCount = Math.ceil(examState.questions.length / courseTopics.length);
      const startIdx = idx * topicQuestionCount;
      const endIdx = Math.min(startIdx + topicQuestionCount, examState.questions.length);
      const topicAnswers = answers.slice(startIdx, endIdx);
      const topicQuestions = examState.questions.slice(startIdx, endIdx);
      
      let correctInTopic = 0;
      topicAnswers.forEach((ans, qIdx) => {
        if (ans === topicQuestions[qIdx]?.correctAnswer) correctInTopic++;
      });
      
      const accuracy = topicAnswers.length > 0 ? (correctInTopic / topicAnswers.length) * 100 : 0;
      
      if (accuracy < 60 && topicAnswers.length > 0) {
        weakTopics.push({
          topic,
          priority: accuracy < 40 ? 'HIGH' : 'MEDIUM',
          missedCount: topicAnswers.length - correctInTopic
        });
      } else if (accuracy > 80 && topicAnswers.length > 0) {
        strongTopics.push(topic);
      }
    });
    
    weakTopics.sort((a, b) => {
      if (a.priority === 'HIGH' && b.priority !== 'HIGH') return -1;
      if (a.priority !== 'HIGH' && b.priority === 'HIGH') return 1;
      return b.missedCount - a.missedCount;
    });
    
    const actionPlan = weakTopics.slice(0, 3).map((topic, idx) => ({
      step: idx + 1,
      topic: topic.topic,
      priority: topic.priority,
      action: topic.priority === 'HIGH' 
        ? `Review ${topic.topic} fundamentals and practice application questions`
        : `Strengthen ${topic.topic} by reviewing key concepts and formulas`
    }));
    
    if (actionPlan.length === 0) {
      actionPlan.push({
        step: 1,
        topic: 'Review',
        priority: 'MEDIUM',
        action: 'Maintain your strong performance by reviewing all topics briefly'
      });
    }
    
    const confidencePercentage = Math.round(
      confidenceLevels.filter(c => c !== null).length > 0
        ? confidenceLevels.reduce((acc, c) => {
            if (c === 'High') return acc + 100;
            if (c === 'Medium') return acc + 60;
            if (c === 'Low') return acc + 30;
            return acc + 50;
          }, 0) / confidenceLevels.filter(c => c !== null).length
        : 50
    );
    
    // Create enhanced feedback object
    const enhancedFeedback = {
      performance: {
        score: correctCount,
        percentage: score,
        readinessScore,
        predictedOutcome
      },
      weakTopics: weakTopics.slice(0, 5),
      strongTopics: strongTopics.slice(0, 5),
      actionPlan,
      performanceInsights: {
        recallStrength,
        speedEfficiency,
        confidenceAccuracyGap: Math.abs(score - confidencePercentage),
        blankRisk
      }
    };
    
    // Create pattern insight
    let patternInsight = '';
    if (recallStrength < 60) {
      patternInsight = 'You struggle with recall under pressure. Practice active recall techniques.';
    } else if (speedEfficiency < 60) {
      patternInsight = 'Time management is your challenge. Practice with stricter time limits.';
    } else if (blankRisk > 30) {
      patternInsight = 'You leave too many questions unanswered. Build confidence to attempt all questions.';
    } else if (weakTopics.length > 0 && weakTopics[0].priority === 'HIGH') {
      patternInsight = `You understand concepts but struggle with ${weakTopics[0].topic}. Focus here for biggest score gain.`;
    } else {
      patternInsight = 'Consistent performance across topics. Maintain momentum with regular practice.';
    }
    
    enhancedFeedback.patternInsight = patternInsight;

    // Save to database
    await updateExamSessionResults(currentSessionId, {
      exam_completed: true,
      score_percentage: score,
      correct_answers: correctCount,
      total_questions: examState.questions.length,
      time_taken_minutes: timeTaken,
      integrity_metrics: {
        copyAttempts: examIntegrity.copyAttempts,
        pasteAttempts: examIntegrity.pasteAttempts,
        tabSwitches: examIntegrity.tabSwitches
      },
      readiness_score: readinessScore,
      confidence_level: overallConfidence,
      enhanced_feedback: enhancedFeedback
    });

    if (isMountedRef.current) {
      setSessions(prev => prev.map(session => 
        session.id === currentSessionId 
          ? {
              ...session,
              exam_completed: true,
              score_percentage: score,
              correct_answers: correctCount,
              total_questions: examState.questions.length,
              time_taken_minutes: timeTaken,
              integrity_metrics: {
                copyAttempts: examIntegrity.copyAttempts,
                pasteAttempts: examIntegrity.pasteAttempts,
                tabSwitches: examIntegrity.tabSwitches
              },
              readiness_score: readinessScore,
              confidence_level: overallConfidence,
              enhanced_feedback: enhancedFeedback
            }
          : session
      ));

      setExamState(prev => prev ? {
        ...prev,
        score,
        isComplete: true,
        endTime: new Date(),
        showResults: true,
        answers: answers,
        confidenceLevels: confidenceLevels,
        timeTaken: timeTaken,
        readinessScore: readinessScore,
        predictedOutcome: predictedOutcome,
        enhancedFeedback: enhancedFeedback
      } : null);
    }

    setTimeout(() => {
      if (isMountedRef.current) {
        loadSessions();
      }
    }, 1000);

  }, [currentSessionId, examIntegrity, examConfig.duration, selectedCourse]);

  useEffect(() => {
    if (examState && !examState.isComplete && timeLeft > 0) {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      
      timerIntervalRef.current = setInterval(() => {
        if (isMountedRef.current) {
          setTimeLeft(prev => {
            if (prev <= 1) {
              if (examState) {
                const remainingAnswers = Array(examState.questions.length - examState.answers.length).fill(-1);
                const allAnswers = [...examState.answers, ...remainingAnswers];
                
                setExamState(prevState => prevState ? {
                  ...prevState,
                  answers: allAnswers,
                  isComplete: true,
                  endTime: new Date()
                } : null);
                
                calculateScore(allAnswers, examState, examState.confidenceLevels);
              }
              
              if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
              }
              
              return 0;
            }
            return prev - 1;
          });
        }
      }, 1000);
      
      return () => {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };
    }
  }, [examState, timeLeft, calculateScore]);

  const handleConfigChange = (field: string, value: any) => {
    setExamConfig(prev => ({...prev, [field]: value}));
  };

  const handleQuestionCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTempQuestionCount(value);
    
    if (value !== '') {
      const max = 60;
      const num = parseNumberInput(value, max, 10);
      handleConfigChange('questionCount', num);
    }
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTempDuration(value);
    
    if (value !== '') {
      const maxDuration = 240;
      const num = parseNumberInput(value, maxDuration, 10);
      handleConfigChange('duration', num);
    }
  };

  const handleQuestionCountBlur = () => {
    if (tempQuestionCount === '') {
      setTempQuestionCount('10');
      handleConfigChange('questionCount', 10);
    }
  };

  const handleDurationBlur = () => {
    if (tempDuration === '') {
      setTempDuration('10');
      handleConfigChange('duration', 10);
    }
  };

  const validateConfig = () => {
    setErrorDetails('');
    
    if (!selectedCourse) {
      setErrorMessage('Please select a registered course');
      return false;
    }
    
    if (!examConfig.fieldOfStudy.trim()) {
      setErrorMessage('Field of Study is required');
      return false;
    }
    
    if (examConfig.questionCount < 1) {
      setErrorMessage('Number of questions must be at least 1');
      return false;
    }
    
    return true;
  };

  const handleStartExam = async () => {
    if (!validateConfig()) return;
    
    setIsLoading(true);
    setErrorMessage('');
    setErrorDetails('');
    
    const newSession = createNewSessionAndReturn();
    
    const title = selectedCourse 
      ? `${selectedCourse.course_name} (${selectedCourse.course_code}) - MCQ Practice`
      : `${examConfig.fieldOfStudy} ${examConfig.level} MCQ Practice`;

    const sessionToSave = {
      ...newSession,
      title,
      examLevel: examConfig.examLevel,
      config: examConfig
    };
    
    const savedSession = await saveExamSession(sessionToSave);
    if (!savedSession) {
      setIsLoading(false);
      return;
    }
    
    const newSessionId = savedSession.id;
    if (isMountedRef.current) {
      setSessions(prev => prev.map(s => s.id === newSession.id ? savedSession : s));
      setCurrentSessionId(newSessionId);
    }

    const configMessage = `Starting ${examConfig.questionCount}-question exam for ${selectedCourse?.course_name || examConfig.fieldOfStudy}...`;
    const userMessage: ChatMessageType = {
      id: Date.now().toString(),
      role: 'user',
      content: configMessage,
      timestamp: new Date(),
      chat_type: 'mcq-exam',
    };

    const newMessages = [...messages, userMessage];
    if (isMountedRef.current) {
      setMessages(newMessages);
    }
    await saveChatMessage(newSessionId, 'user', userMessage.content);

    try {
      // Use the imported prompt function with course data
      const examPrompt = getMCQExamPromptWithCourseData(selectedCourse, examConfig, userProfile);
      
      const systemMessage = `You are an MCQ Exam Generator for BB EdTech. Generate exactly ${examConfig.questionCount} multiple-choice questions.

CRITICAL: You MUST generate EXACTLY ${examConfig.questionCount} questions - no more, no less.

IMPORTANT: Use the course syllabus and lecturer keywords provided to create relevant questions.

Format your response as valid JSON:
{
  "questions": [
    {
      "question": "Clear question text",
      "options": ["Option A", "Option B", "Option C", "Option D"],
      "correctAnswer": 0,
      "explanation": "Detailed explanation"
    }
  ]
}`;

      const response = await callOpenRouterApi(examPrompt, userProfile, systemMessage);
      
      let cleanResponse = response.trim();
      cleanResponse = cleanResponse.replace(/```json\n?/g, '').replace(/```\n?/g, '');
      
      const examData = JSON.parse(cleanResponse);

      if (!examData.questions || examData.questions.length === 0) {
        throw new Error('No questions generated');
      }
      
      if (examData.questions.length !== examConfig.questionCount) {
        if (examData.questions.length > examConfig.questionCount) {
          examData.questions = examData.questions.slice(0, examConfig.questionCount);
        } else {
          throw new Error(`Expected ${examConfig.questionCount} questions but got ${examData.questions.length}`);
        }
      }

      if (isMountedRef.current) {
        setExamState({
          questions: examData.questions,
          currentQuestion: 0,
          answers: Array(examConfig.questionCount).fill(-1),
          confidenceLevels: Array(examConfig.questionCount).fill(null),
          isComplete: false,
          score: 0,
          startTime: new Date(),
          endTime: null,
          showResults: false,
          timeTaken: 0,
          readinessScore: 0,
          predictedOutcome: 'Borderline'
        });

        setTimeLeft(examConfig.duration * 60);
        setExamStarted(true);
      }

      const assistantMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `## 📚 Exam Started! ⏱️\n\n**Course:** ${selectedCourse?.course_name} (${selectedCourse?.course_code})\n**Field of Study:** ${examConfig.fieldOfStudy}\n**Level:** ${examConfig.level}\n\nYou have **${examConfig.duration} minutes** to complete **${examConfig.questionCount}** questions.\n\n### Instructions:\n1. Read each question carefully\n2. Select the best answer\n3. ${showConfidenceCheck ? 'Rate your confidence after each answer' : ''}\n4. Use navigation buttons to move between questions\n5. Submit when you're ready\n6. Timer will auto-submit when time expires\n\n**Good luck!** 🍀`,
        timestamp: new Date(),
        chat_type: 'mcq-exam',
      };

      const updatedMessages = [...newMessages, assistantMessage];
      if (isMountedRef.current) {
        setMessages(updatedMessages);
      }
      await saveChatMessage(newSessionId, 'assistant', assistantMessage.content);

    } catch (error: any) {
      console.error('Error generating exam:', error);
      setErrorMessage('Failed to generate exam. Please try again with shorter course details or fewer questions.');
      
      const errorMsg: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error while generating the exam. Please try again with a different course or fewer questions.',
        timestamp: new Date(),
        chat_type: 'mcq-exam',
      };
      
      const updatedMessages = [...newMessages, errorMsg];
      if (isMountedRef.current) {
        setMessages(updatedMessages);
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };
  
  const createNewSessionAndReturn = () => {
    const tempSession: ExamSession = {
      id: `temp-${Date.now()}`,
      title: 'New MCQ Exam',
      timestamp: new Date(),
      messages: [],
      examLevel: 'university',
      config: examConfig,
      isTemporary: true,
      exam_completed: false,
    };
    
    if (isMountedRef.current) {
      setSessions(prev => [tempSession, ...prev]);
      setCurrentSessionId(tempSession.id);
      setMessages([]);
      setExamStarted(false);
      setExamState(null);
      setShowCorrectAnswers(false);
    }
    
    return tempSession;
  };

  const handleExamAnswer = (answerIndex: number) => {
    if (!examState || examState.isComplete) return;

    const newAnswers = [...examState.answers];
    newAnswers[examState.currentQuestion] = answerIndex;

    if (isMountedRef.current) {
      setExamState(prev => prev ? {
        ...prev,
        answers: newAnswers
      } : null);
    }
  };

  const handleConfidenceRating = (rating: 'High' | 'Medium' | 'Low') => {
    if (!examState || examState.isComplete) return;

    const newConfidenceLevels = [...examState.confidenceLevels];
    newConfidenceLevels[examState.currentQuestion] = rating;

    if (isMountedRef.current) {
      setExamState(prev => prev ? {
        ...prev,
        confidenceLevels: newConfidenceLevels
      } : null);
    }
  };

  const navigateToQuestion = (index: number) => {
    if (!examState || examState.isComplete) return;
    
    if (isMountedRef.current) {
      setExamState(prev => prev ? {
        ...prev,
        currentQuestion: index
      } : null);
    }
  };

  const goToPrevious = () => {
    if (!examState || examState.currentQuestion === 0) return;
    
    if (isMountedRef.current) {
      setExamState(prev => prev ? {
        ...prev,
        currentQuestion: prev.currentQuestion - 1
      } : null);
    }
  };

  const goToNext = () => {
    if (!examState || examState.currentQuestion === examState.questions.length - 1) return;
    
    if (isMountedRef.current) {
      setExamState(prev => prev ? {
        ...prev,
        currentQuestion: prev.currentQuestion + 1
      } : null);
    }
  };

  const handleSubmitExam = () => {
    if (!examState || isLoading) return;
    
    const unansweredCount = examState.answers.filter(a => a === -1).length;
    if (window.confirm(`Are you sure you want to submit? ${unansweredCount} questions unanswered.`)) {
      if (isMountedRef.current) {
        setIsLoading(true);
      }
      
      const finalAnswers = [...examState.answers];
      const finalConfidenceLevels = [...examState.confidenceLevels];
      
      if (isMountedRef.current) {
        setExamState(prev => prev ? {
          ...prev,
          answers: finalAnswers,
          isComplete: true,
          endTime: new Date()
        } : null);
      }
      
      calculateScore(finalAnswers, examState, finalConfidenceLevels).finally(() => {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      });
    }
  };

  const handleTakeAnotherPractice = () => {
    createNewSessionAndReturn();
    setShowCorrectAnswers(false);
    setSelectedCourse(null);
  };

  const handleSeeCorrectAnswers = () => {
    setShowCorrectAnswers(true);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const calculateAnsweredCount = () => {
    if (!examState) return 0;
    return examState.answers.filter(a => a !== -1).length;
  };

  const switchToSession = async (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    if (session) {
      if (isMountedRef.current) {
        setCurrentSessionId(sessionId);
        setMessages(session.messages);
        setExamStarted(false);
        setExamState(null);
        setShowCorrectAnswers(false);
        
        if (session.config) {
          setExamConfig(session.config);
        }
        
        if (session.enhanced_feedback && session.exam_completed) {
          setExamStarted(true);
        }
      }
    }
  };

  const deleteSession = async (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    if (session?.isTemporary) {
      if (isMountedRef.current) {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
      }
    } else {
      await supabase.from('exam_sessions').delete().eq('id', sessionId);
      await supabase.from('exam_messages').delete().eq('session_id', sessionId);
      if (isMountedRef.current) {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
      }
    }

    if (currentSessionId === sessionId) {
      const remainingSessions = sessions.filter(s => s.id !== sessionId);
      if (remainingSessions.length > 0) {
        switchToSession(remainingSessions[0].id);
      } else {
        createNewSessionAndReturn();
      }
    }
  };

  const handleChatSubmit = async () => {
    if (!chatInput.trim() || isLoading) return;
    
    if (isMountedRef.current) {
      setIsLoading(true);
    }
    setErrorMessage('');
    
    const userMessage: ChatMessageType = {
      id: Date.now().toString(),
      role: 'user',
      content: chatInput,
      timestamp: new Date(),
      chat_type: 'mcq-exam',
    };
    
    const newMessages = [...messages, userMessage];
    if (isMountedRef.current) {
      setMessages(newMessages);
    }
    await saveChatMessage(currentSessionId, 'user', chatInput);
    setChatInput('');
    
    try {
      const systemMessage = "You are a helpful assistant for BB EdTech's MCQ Exam Simulator. Answer questions about exam results, provide explanations, and help with learning concepts. Use proper Markdown formatting and LaTeX for mathematical expressions.";
      
      const response = await callOpenRouterApi(chatInput, userProfile, systemMessage);
      
      const assistantMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response,
        timestamp: new Date(),
        chat_type: 'mcq-exam',
      };
      
      if (isMountedRef.current) {
        setMessages(prev => [...prev, assistantMessage]);
      }
      await saveChatMessage(currentSessionId, 'assistant', response);
      
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage('Failed to get response. Please try again.');
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  // Render functions
  const renderTimer = () => {
    if (!examStarted || !timeLeft || !examState || examState.isComplete) return null;
    
    const totalSeconds = examConfig.duration * 60;
    const percentage = (timeLeft / totalSeconds) * 100;
    const isWarning = timeLeft < 300;
    
    return (
      <div className="exam-timer-section">
        <div className="timer-spinner">
          <svg className="timer-svg" viewBox="0 0 100 100">
            <circle className="timer-background" cx="50" cy="50" r="45"></circle>
            <circle 
              className={`timer-progress ${isWarning ? 'warning' : ''}`}
              cx="50" cy="50" r="45"
              strokeDasharray="283"
              strokeDashoffset={283 - (283 * percentage / 100)}
            ></circle>
          </svg>
          <div className="timer-text">
            <div className="timer-digits">{formatTime(timeLeft)}</div>
            <div className="timer-label">Time Left</div>
          </div>
        </div>
      </div>
    );
  };

  const renderProgressStats = () => {
    if (!examState || examState.isComplete) return null;
    
    const answeredCount = calculateAnsweredCount();
    const totalQuestions = examState.questions.length;
    const progressPercentage = Math.round((answeredCount / totalQuestions) * 100);
    
    return (
      <div className="progress-stats">
        <div className="stat-item">
          <strong>Answered</strong>
          <span>{answeredCount}/{totalQuestions}</span>
        </div>
        <div className="stat-item">
          <strong>Progress</strong>
          <span>{progressPercentage}%</span>
        </div>
        <div className="stat-item">
          <strong>Integrity</strong>
          <span>{examIntegrity.tabSwitches}</span>
        </div>
      </div>
    );
  };

  const renderConfidenceRating = () => {
    if (!showConfidenceCheck || !examState || examState.isComplete) return null;
    
    const currentConfidence = examState.confidenceLevels[examState.currentQuestion];
    
    return (
      <div className="confidence-rating-container">
        <div className="confidence-prompt">
          <span className="confidence-icon">🤔</span>
          <span className="confidence-label">How confident were you about this answer?</span>
        </div>
        <div className="confidence-buttons">
          <button
            className={`confidence-btn high ${currentConfidence === 'High' ? 'active' : ''}`}
            onClick={() => handleConfidenceRating('High')}
          >
            <span className="confidence-emoji">😊</span>
            <span>High</span>
          </button>
          <button
            className={`confidence-btn medium ${currentConfidence === 'Medium' ? 'active' : ''}`}
            onClick={() => handleConfidenceRating('Medium')}
          >
            <span className="confidence-emoji">😐</span>
            <span>Medium</span>
          </button>
          <button
            className={`confidence-btn low ${currentConfidence === 'Low' ? 'active' : ''}`}
            onClick={() => handleConfidenceRating('Low')}
          >
            <span className="confidence-emoji">😰</span>
            <span>Low</span>
          </button>
        </div>
      </div>
    );
  };

  const renderExamQuestion = () => {
    if (!examState || !examState.questions[examState.currentQuestion]) return null;
    
    const currentQuestion = examState.questions[examState.currentQuestion];
    const userAnswer = examState.answers[examState.currentQuestion];
    
    return (
      <>
        <div className="exam-question">
          <MarkdownRenderer content={currentQuestion.question} theme={theme} />
        </div>
        
        <div className="exam-options">
          {currentQuestion.options.map((option, idx) => (
            <div
              key={idx}
              className={`exam-option ${userAnswer === idx ? 'selected' : ''}`}
              onClick={() => handleExamAnswer(idx)}
            >
              <div className="option-letter">{String.fromCharCode(65 + idx)}</div>
              <div className="option-text">
                <MarkdownRenderer content={option} theme={theme} />
              </div>
            </div>
          ))}
        </div>
        
        {renderConfidenceRating()}
      </>
    );
  };

  const renderQuestionBoxes = () => {
    if (!examState || examState.isComplete) return null;
    
    return (
      <div className="question-boxes-container">
        <h4>Questions</h4>
        <div className="question-boxes-grid">
          {examState.questions.map((_, index) => {
            const isAnswered = examState.answers[index] !== -1;
            const isCurrent = examState.currentQuestion === index;
            const confidence = examState.confidenceLevels[index];
            let confidenceClass = '';
            if (confidence === 'High') confidenceClass = 'high';
            if (confidence === 'Medium') confidenceClass = 'medium';
            if (confidence === 'Low') confidenceClass = 'low';
            
            const boxClass = `question-box ${isCurrent ? 'current' : ''} ${isAnswered ? 'answered' : 'unanswered'} ${confidenceClass}`;
            
            return (
              <button
                key={index}
                className={boxClass}
                onClick={() => navigateToQuestion(index)}
                disabled={examState.isComplete}
                title={confidence ? `Confidence: ${confidence}` : 'Not rated'}
              >
                {index + 1}
              </button>
            );
          })}
        </div>
        <div className="question-legend">
          <div className="legend-item">
            <div className="legend-color current"></div>
            <span>Current</span>
          </div>
          <div className="legend-item">
            <div className="legend-color answered"></div>
            <span>Answered</span>
          </div>
          <div className="legend-item">
            <div className="legend-color unanswered"></div>
            <span>Unanswered</span>
          </div>
          {showConfidenceCheck && (
            <>
              <div className="legend-item">
                <div className="legend-color high"></div>
                <span>High Confidence</span>
              </div>
              <div className="legend-item">
                <div className="legend-color medium"></div>
                <span>Medium Confidence</span>
              </div>
              <div className="legend-item">
                <div className="legend-color low"></div>
                <span>Low Confidence</span>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderCourseSelection = () => {
    if (loadingCourses) {
      return (
        <div className="loading-courses">
          <div className="loading-spinner-small"></div>
          <span>Loading your registered courses...</span>
        </div>
      );
    }

    if (registeredCourses.length === 0) {
      return (
        <div className="no-courses-message">
          <p>⚠️ No registered courses found.</p>
          <p>Please go to <strong>Course Registration</strong> to add your courses first.</p>
          <button 
            className="go-to-courses-btn"
            onClick={() => window.location.href = '/courses'}
          >
            📚 Go to Course Registration
          </button>
        </div>
      );
    }

    return (
      <div className="course-selection-container">
        <label className="form-label">Select Registered Course *</label>
        <select
          className="form-select"
          value={selectedCourse?.id || ''}
          onChange={(e) => {
            const course = registeredCourses.find(c => c.id === e.target.value);
            setSelectedCourse(course || null);
          }}
        >
          <option value="">-- Select a course --</option>
          {registeredCourses.map(course => (
            <option key={course.id} value={course.id}>
              {course.course_name} ({course.course_code})
            </option>
          ))}
        </select>
        {selectedCourse && (
          <div className="selected-course-info">
            <div className="course-detail">
              <strong>📖 Course:</strong> {selectedCourse.course_name}
            </div>
            <div className="course-detail">
              <strong>🔖 Code:</strong> {selectedCourse.course_code}
            </div>
            {selectedCourse.lecturer_keywords && selectedCourse.lecturer_keywords.length > 0 && (
              <div className="course-detail">
                <strong>🔑 Keywords:</strong> {selectedCourse.lecturer_keywords.join(', ')}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderConfigDetails = () => {
    return (
      <div className="config-step">
        <div className="step-header">
          <h3>Configure Your Exam</h3>
        </div>
        
        <div className="config-form">
          {/* Course Selection - from registered courses */}
          <div className="form-group full-width">
            {renderCourseSelection()}
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Field of Study *</label>
              <input
                type="text"
                value={examConfig.fieldOfStudy}
                onChange={(e) => handleConfigChange('fieldOfStudy', e.target.value)}
                placeholder="e.g., Computer Science, Medicine, Engineering"
                className="form-input"
              />
            </div>
            
            <div className="form-group">
              <label>Level *</label>
              <select
                value={examConfig.level}
                onChange={(e) => handleConfigChange('level', e.target.value)}
                className="form-input"
              >
                <option value="Undergraduate">Undergraduate</option>
                <option value="Postgraduate">Postgraduate</option>
                <option value="Researcher">Researcher</option>
                <option value="Professional">Professional</option>
              </select>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Number of Questions</label>
              <input
                type="number"
                min="1"
                max="60"
                value={tempQuestionCount}
                onChange={handleQuestionCountChange}
                onBlur={handleQuestionCountBlur}
                placeholder="10"
                className="form-input"
              />
              <div className="form-hint">Range: 1-60</div>
            </div>
            
            <div className="form-group">
              <label>Duration (minutes)</label>
              <input
                type="number"
                min="1"
                max="240"
                value={tempDuration}
                onChange={handleDurationChange}
                onBlur={handleDurationBlur}
                placeholder="10"
                className="form-input"
              />
              <div className="form-hint">Range: 1-240 minutes</div>
            </div>
          </div>
          
          <div className="form-group full-width">
            <label>Exam Confidence Level</label>
            <select
              value={examConfig.examConfidence}
              onChange={(e) => handleConfigChange('examConfidence', e.target.value)}
              className="form-input"
            >
              <option value="Low">Low - Basic understanding, need practice</option>
              <option value="Medium">Medium - Comfortable with most topics</option>
              <option value="High">High - Confident, ready for final exam</option>
            </select>
            <div className="form-hint">This helps adjust question difficulty and feedback.</div>
          </div>
          
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showConfidenceCheck}
                onChange={(e) => setShowConfidenceCheck(e.target.checked)}
              />
              <span>Enable confidence tracking after each question</span>
            </label>
            <div className="form-hint">Helps identify anxiety patterns and weak areas.</div>
          </div>
        </div>
        
        <div className="config-navigation">
          <button
            className="start-exam-btn"
            onClick={handleStartExam}
            disabled={isLoading || !selectedCourse}
          >
            {isLoading ? (
              <>
                <span className="loading-spinner"></span>
                Creating Practice Test...
              </>
            ) : (
              `🚀 Start MCQ Practice`
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderSidebar = () => (
    <div className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
      <div className="sidebar-header">
        <div className="logo">📚</div>
        {sidebarOpen && <span className="app-name">MCQ Exams</span>}
        <button
          className="sidebar-toggle-inside"
          onClick={() => setSidebarOpen(false)}
          title="Close sidebar"
        >
          ◀
        </button>
      </div>
      
      {sidebarOpen && (
        <>
          <div className="theme-toggle-sidebar">
            <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
            </button>
          </div>
          
          <button className="new-exam-btn" onClick={createNewSessionAndReturn}>
            ➕ New Exam
          </button>
          
          <div className="search-container">
            <input
              type="text"
              placeholder="Search exams..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="exam-history">
            <h4>Exam History</h4>
            {sessions
              .filter(session => 
                session.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                session.examLevel.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map((session) => (
                <div
                  key={session.id}
                  className={`exam-session-item ${currentSessionId === session.id ? 'active' : ''}`}
                  onClick={() => switchToSession(session.id)}
                >
                  <div className="session-title">
                    <span className="session-title-text">{session.title}</span>
                    {session.exam_completed && session.score_percentage !== undefined && (
                      <span className={`session-score ${session.score_percentage >= 70 ? 'pass' : 'fail'}`}>
                        {session.score_percentage}%
                      </span>
                    )}
                    {session.isTemporary && ' (New)'}
                  </div>
                  <div className="session-info">
                    <span className="session-timestamp">
                      {new Date(session.timestamp).toLocaleDateString()}
                    </span>
                    <span className={`exam-level-badge ${session.examLevel}`}>
                      {session.examLevel === 'secondary' ? 'Secondary' : 'University'}
                    </span>
                  </div>
                  <button
                    className="delete-session-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSession(session.id);
                    }}
                  >
                    🗑️
                  </button>
                </div>
              ))}
          </div>
        </>
      )}
    </div>
  );

  const renderMainContent = () => {
    if (showCorrectAnswers && examState?.isComplete) {
      return renderCorrectAnswers();
    }
    
    if (examStarted && examState) {
      return renderExamInterface();
    } else {
      return (
        <div className="config-details-container">
          {renderConfigDetails()}
        </div>
      );
    }
  };

  const renderCorrectAnswers = () => {
    if (!examState || !showCorrectAnswers) return null;
    
    return (
      <div className="correct-answers-container">
        <div className="correct-answers-header">
          <h3>📝 Detailed Answer Review</h3>
          <button 
            className="close-answers-btn"
            onClick={() => setShowCorrectAnswers(false)}
          >
            ✕
          </button>
        </div>
        <div className="correct-answers-list">
          {examState.questions.map((q, idx) => {
            const userAnswer = examState.answers[idx];
            const isCorrect = userAnswer === q.correctAnswer;
            const userAnswerText = userAnswer >= 0 ? q.options[userAnswer] : 'Not answered';
            const confidence = examState.confidenceLevels[idx];
            
            return (
              <div key={idx} className={`answer-review-card ${isCorrect ? 'correct' : 'incorrect'}`}>
                <div className="answer-review-header">
                  <span className="question-num">Question {idx + 1}</span>
                  <span className={`answer-status ${isCorrect ? 'correct' : 'incorrect'}`}>
                    {isCorrect ? '✅ Correct' : '❌ Incorrect'}
                  </span>
                  {confidence && (
                    <span className={`confidence-badge ${confidence.toLowerCase()}`}>
                      Confidence: {confidence}
                    </span>
                  )}
                </div>
                <div className="answer-review-question">
                  <MarkdownRenderer content={q.question} theme={theme} />
                </div>
                <div className="answer-review-details">
                  <div className="correct-answer-detail">
                    <strong>Correct Answer:</strong> {q.options[q.correctAnswer]}
                  </div>
                  <div className="user-answer-detail">
                    <strong>Your Answer:</strong> {userAnswerText}
                  </div>
                  <div className="explanation-detail">
                    <strong>🧠 Why it is correct:</strong>
                    <MarkdownRenderer content={q.explanation} theme={theme} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="correct-answers-actions">
          <button className="take-another-btn" onClick={handleTakeAnotherPractice}>
            📝 Take Another Practice
          </button>
          <button className="back-to-chat-btn" onClick={() => setShowCorrectAnswers(false)}>
            💬 Back to Chat
          </button>
        </div>
      </div>
    );
  };

  function renderExamInterface() {
    if (!examState) return null;
    
    if (!examState.isComplete) {
      return (
        <>
          <div className="exam-header">
            {renderTimer()}
            {renderProgressStats()}
          </div>
          
          <div className="exam-content-wrapper">
            <div className="exam-question-container">
              <div className="exam-question-header">
                <div className="question-number">Question {examState.currentQuestion + 1} of {examState.questions.length}</div>
                <div className="question-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{width: `${((examState.currentQuestion + 1) / examState.questions.length) * 100}%`}}
                    ></div>
                  </div>
                </div>
              </div>
              
              {renderExamQuestion()}
              
              <div className="exam-navigation">
                <button
                  className="nav-btn prev-btn"
                  onClick={goToPrevious}
                  disabled={examState.currentQuestion === 0}
                >
                  ← Previous
                </button>
                
                <div className="question-status">
                  <span className={`status-indicator ${examState.answers[examState.currentQuestion] !== -1 ? 'answered' : 'unanswered'}`}>
                    {examState.answers[examState.currentQuestion] !== -1 ? '✓ Answered' : '○ Unanswered'}
                  </span>
                </div>
                
                <button
                  className="nav-btn next-btn"
                  onClick={goToNext}
                  disabled={examState.currentQuestion === examState.questions.length - 1}
                >
                  Next →
                </button>
              </div>
              
              <div className="exam-submit-section">
                <button
                  className="submit-exam-btn"
                  onClick={handleSubmitExam}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="loading-spinner"></span>
                      Processing...
                    </>
                  ) : (
                    '🚀 Submit Exam'
                  )}
                </button>
                <div className="submit-note">
                  {examState.answers.filter(a => a === -1).length} questions unanswered
                </div>
              </div>
            </div>
            
            {renderQuestionBoxes()}
          </div>
        </>
      );
    } else {
      // Use premium feedback dashboard when exam is complete
      return (
        <div className="exam-results-container">
          {examState.enhancedFeedback && (
            <MCQFeedbackDashboard
              performance={examState.enhancedFeedback.performance}
              weakTopics={examState.enhancedFeedback.weakTopics}
              strongTopics={examState.enhancedFeedback.strongTopics}
              actionPlan={examState.enhancedFeedback.actionPlan}
              performanceInsights={examState.enhancedFeedback.performanceInsights}
              patternInsight={examState.enhancedFeedback.patternInsight}
              questions={examState.questions}
              answers={examState.answers}
              theme={theme}
              onTakeAnother={handleTakeAnotherPractice}
              onSeeCorrectAnswers={handleSeeCorrectAnswers}
              onChat={() => {
                chatContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
              }}
            />
          )}
          
          {/* Keep chat interface for additional discussion */}
          <div className="exam-chat-interface">
            <div className="chat-messages-container" ref={chatContainerRef}>
              {messages.map((message) => (
                <div key={message.id} className="message-wrapper">
                  <ChatMessage 
                    message={message}
                    onSpeak={(text) => speak(text, message.id)}
                    isSpeaking={isMessageSpeaking(message.id)}
                    onShare={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/exam/message/${message.id}`);
                    }}
                  />
                </div>
              ))}
              {isLoading && (
                <div className="typing-indicator">
                  <div className="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <span className="typing-text">Processing...</span>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            
            <div className="chat-input-container">
              <InputField
                value={chatInput}
                onChange={setChatInput}
                onSubmit={handleChatSubmit}
                loading={isLoading}
                placeholder="Ask about your exam results or request explanations..."
              />
            </div>
          </div>
        </div>
      );
    }
  }

  return (
    <div className={`mcq-exam-simulator ${theme}`}>
      <div className="background-container">
        {theme === 'light' ? (
          <div className="morning-sky"></div>
        ) : (
          <div className="night-sky"></div>
        )}
      </div>

      <div className="mcq-exam-layout">
        {renderSidebar()}

        <div className={`main-content ${sidebarOpen ? 'sidebar-open' : ''}`}>
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(true)}
          >
            ▶
          </button>

          <div className="exam-header-section">
            <h1 className="exam-main-title">📚 MCQ Exam Simulator</h1>
            <p className="exam-subtitle">Select a registered course and configure your personalized MCQ practice test</p>
          </div>

          {errorMessage && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              <div className="error-content">
                <span className="error-text">{errorMessage}</span>
                {errorDetails && (
                  <span className="error-details">({errorDetails})</span>
                )}
              </div>
            </div>
          )}

          <div className="exam-main-area" key={`content-${examStarted}-${currentSessionId}`}>
            {renderMainContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MCQExamSimulator;